import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './P2pTradeBuyPendingPay.css';

const mockOrderData = {
  orderId: '9226448442254659',
  merchant: {
    name: 'Daniel Abraham',
    initial: 'D',
    verified: true,
    fullName: 'DANIEL ABRAHAM'
  },
  timeRemaining: 1792, // seconds (29:52)
  orderDetails: {
    quantity: '300.00',
    crypto: 'USDT',
    amountFiat: '18450.00',
    currency: 'PHP',
    price: '61.5',
    priceUnit: 'PHP/USDT'
  },
  paymentMethod: {
    bankName: 'FBN',
    accountName: 'Rene Lorenzo',
    bank: 'FBN',
    accountNumber: '09951629018'
  }
};

const P2pTradeBuyPendingPay: React.FC = () => {
  const navigate = useNavigate();
  const [timeRemaining, setTimeRemaining] = useState(mockOrderData.timeRemaining);

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 0) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const progressPercentage = (timeRemaining / mockOrderData.timeRemaining) * 100;

  const handleBack = () => {
    navigate(-1);
  };

  const handleChat = () => {
    console.log('Opening chat...');
  };

  const handleCopyOrderId = () => {
    navigator.clipboard.writeText(mockOrderData.orderId);
  };

  const handleCopyAccountName = () => {
    navigator.clipboard.writeText(mockOrderData.paymentMethod.accountName);
  };

  const handleCopyAccountNumber = () => {
    navigator.clipboard.writeText(mockOrderData.paymentMethod.accountNumber);
  };

  const handlePaymentConfirm = () => {
    console.log('Payment confirmed');
  };

  const handleCancel = () => {
    console.log('Order cancelled');
  };

  const handleDispute = () => {
    console.log('Dispute opened');
  };

  return (
    <div className="pending-pay-container">
      <header className="pending-pay-header">
        <button className="pending-pay-back-button" onClick={handleBack} aria-label="Go back">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        
        <h1 className="pending-pay-title">Buy USDT</h1>

        <button className="pending-pay-chat-button" onClick={handleChat} aria-label="Chat">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M17 8.5C17 12.366 13.866 15.5 10 15.5C8.95 15.5 7.951 15.28 7.049 14.888L3 16L4.112 11.951C3.72 11.049 3.5 10.05 3.5 9C3.5 5.134 6.634 2 10.5 2C14.366 2 17.5 5.134 17.5 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      </header>

      <div className="pending-pay-progress-container">
        <div className="pending-pay-progress-bar">
          <div 
            className="pending-pay-progress-fill" 
            style={{ width: `${progressPercentage}%` }}
          />
        </div>
      </div>

      <div className="pending-pay-content">
        <div className="pending-pay-merchant-card">
          <div className="pending-pay-merchant-avatar">
            {mockOrderData.merchant.initial}
          </div>
          
          <div className="pending-pay-merchant-info">
            <div className="pending-pay-merchant-name">
              {mockOrderData.merchant.name}
              {mockOrderData.merchant.verified && (
                <svg className="pending-pay-verified-badge" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M10 0L11.5 6.5L18 8L11.5 9.5L10 16L8.5 9.5L2 8L8.5 6.5L10 0Z" fill="#22c35d"/>
                </svg>
              )}
            </div>
            <div className="pending-pay-merchant-label">Merchant</div>
          </div>

          <h2 className="pending-pay-order-heading">Order Created</h2>
          
          <p className="pending-pay-timer-label">
            Order will be cancelled if payment isn't made within:
          </p>

          <div className="pending-pay-timer-card">
            <div className="pending-pay-timer-display">{formatTime(timeRemaining)}</div>
            <div className="pending-pay-timer-text">Time Remaining</div>
          </div>
        </div>

        <div className="pending-pay-order-id-card">
          <div className="pending-pay-order-id-label">Order ID</div>
          <div className="pending-pay-order-id-value">{mockOrderData.orderId}</div>
          <button 
            className="pending-pay-copy-button" 
            onClick={handleCopyOrderId}
            aria-label="Copy order ID"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <rect x="5" y="5" width="9" height="9" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
              <path d="M3 11V3C3 2.44772 3.44772 2 4 2H10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        <div className="pending-pay-details-card">
          <h3 className="pending-pay-details-heading">Order Details</h3>
          
          <div className="pending-pay-detail-row">
            <span className="pending-pay-detail-label">Quantity</span>
            <div className="pending-pay-detail-value-group">
              <span className="pending-pay-detail-amount">{mockOrderData.orderDetails.quantity}</span>
              <span className="pending-pay-detail-crypto">{mockOrderData.orderDetails.crypto}</span>
            </div>
          </div>

          <div className="pending-pay-divider" />

          <div className="pending-pay-detail-row">
            <span className="pending-pay-detail-label">Amount (Fiat)</span>
            <div className="pending-pay-detail-value-group">
              <span className="pending-pay-detail-amount">{mockOrderData.orderDetails.amountFiat}</span>
              <span className="pending-pay-detail-currency">{mockOrderData.orderDetails.currency}</span>
            </div>
          </div>

          <div className="pending-pay-divider" />

          <div className="pending-pay-detail-row">
            <span className="pending-pay-detail-label">Price</span>
            <div className="pending-pay-detail-value-group">
              <span className="pending-pay-detail-amount">{mockOrderData.orderDetails.price}</span>
              <span className="pending-pay-detail-unit">{mockOrderData.orderDetails.priceUnit}</span>
            </div>
          </div>
        </div>

        <div className="pending-pay-payment-card">
          <div className="pending-pay-payment-merchant">
            <span className="pending-pay-payment-label">Merchant Name</span>
            <span className="pending-pay-payment-value">{mockOrderData.merchant.fullName}</span>
          </div>

          <h3 className="pending-pay-payment-heading">Payment Method</h3>

          <div className="pending-pay-payment-method-box">
            <div className="pending-pay-bank-header">
              <span className="pending-pay-bank-name">{mockOrderData.paymentMethod.bankName}</span>
              <div className="pending-pay-bank-badge" />
            </div>

            <div className="pending-pay-payment-info-row">
              <span className="pending-pay-payment-info-label">Account Name</span>
              <span className="pending-pay-payment-info-value">{mockOrderData.paymentMethod.accountName}</span>
              <button 
                className="pending-pay-copy-icon-button" 
                onClick={handleCopyAccountName}
                aria-label="Copy account name"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <rect x="5" y="5" width="9" height="9" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
                  <path d="M3 11V3C3 2.44772 3.44772 2 4 2H10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </button>
            </div>

            <div className="pending-pay-payment-info-row">
              <span className="pending-pay-payment-info-label">Bank</span>
              <span className="pending-pay-payment-info-value">{mockOrderData.paymentMethod.bank}</span>
            </div>

            <div className="pending-pay-payment-info-row">
              <span className="pending-pay-payment-info-label">Account Number</span>
              <span className="pending-pay-payment-info-value pending-pay-account-number">{mockOrderData.paymentMethod.accountNumber}</span>
              <button 
                className="pending-pay-copy-icon-button" 
                onClick={handleCopyAccountNumber}
                aria-label="Copy account number"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <rect x="5" y="5" width="9" height="9" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
                  <path d="M3 11V3C3 2.44772 3.44772 2 4 2H10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="pending-pay-action-bar">
        <button className="pending-pay-primary-button" onClick={handlePaymentConfirm}>
          I have made payment
        </button>
        
        <div className="pending-pay-secondary-buttons">
          <button className="pending-pay-cancel-button" onClick={handleCancel}>
            Cancel
          </button>
          <button className="pending-pay-dispute-button" onClick={handleDispute}>
            Dispute
          </button>
        </div>
      </div>
    </div>
  );
};

export default P2pTradeBuyPendingPay;